/**
 * @file Route-local state for the Shared Calendar domain.
 */

const calendarState = {
    baseEntries: [],
    entries: [],
    optimisticEntries: [],
    listenerStatus: "idle",
    listenerError: null,
    visibleStartMonth: null,
    visibleEndMonth: null,
    loadedMonthKeys: [],
    overflowMonthKeys: [],
};

function getEntryIdentity(entry = {}) {
    return [
        entry.sourceType || entry.renderKind || "",
        entry.sourceId || "",
        entry.projectionSlot || entry.occurrenceId || "",
    ].join(":");
}

function reconcileEntries({ dropResolvedOptimistic = false } = {}) {
    const baseIdentities = new Set(calendarState.baseEntries.map(getEntryIdentity));
    if (dropResolvedOptimistic) {
        calendarState.optimisticEntries = calendarState.optimisticEntries.filter((entry) => {
            const isTemporary = String(entry.sourceId || "").startsWith("optimistic-");
            return isTemporary || !baseIdentities.has(getEntryIdentity(entry));
        });
    }
    const optimisticIdentities = new Set(
        calendarState.optimisticEntries.map(getEntryIdentity),
    );
    calendarState.entries = [
        ...calendarState.baseEntries.filter((entry) => {
            return !optimisticIdentities.has(getEntryIdentity(entry));
        }),
        ...calendarState.optimisticEntries,
    ];
}

/**
 * Returns the mutable Calendar route-local state object.
 * @returns {object} Calendar state.
 */
export function getCalendarState() {
    return calendarState;
}

/**
 * Stores display-safe Calendar entries for the active route window.
 * @param {Array<object>} entries Display-safe calendar entry rows.
 * @returns {void}
 */
export function setCalendarEntries(entries) {
    calendarState.baseEntries = Array.isArray(entries) ? entries : [];
    reconcileEntries({ dropResolvedOptimistic: true });
}

/**
 * Adds or updates a route-local optimistic Calendar entry.
 * @param {object} entry Display-safe optimistic entry.
 * @returns {string} Optimistic entry id.
 */
export function upsertOptimisticCalendarEntry(entry) {
    const optimisticId = entry.optimisticId ||
        `optimistic-entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const nextEntry = {
        ...entry,
        optimisticId,
    };
    const nextIdentity = getEntryIdentity(nextEntry);
    calendarState.optimisticEntries = calendarState.optimisticEntries
        .filter((existing) => {
            return existing.optimisticId !== optimisticId &&
                getEntryIdentity(existing) !== nextIdentity;
        });
    calendarState.optimisticEntries.push(nextEntry);
    reconcileEntries();
    return optimisticId;
}

/**
 * Removes a route-local optimistic Calendar entry.
 * @param {string} optimisticId Optimistic entry id.
 * @returns {void}
 */
export function removeOptimisticCalendarEntry(optimisticId) {
    calendarState.optimisticEntries = calendarState.optimisticEntries
        .filter((entry) => entry.optimisticId !== optimisticId);
    reconcileEntries();
}

/**
 * Removes a Calendar source entry from the route-local cache.
 * @param {string} sourceType Calendar source type.
 * @param {string} sourceId Source document id.
 * @returns {void}
 */
export function removeCalendarSourceEntry(sourceType, sourceId) {
    const normalizedSourceType = sourceType || "";
    const normalizedSourceId = sourceId || "";
    const isSourceMatch = (entry) => {
        return (entry.sourceType || entry.renderKind || "") === normalizedSourceType &&
            (entry.sourceId || "") === normalizedSourceId;
    };
    calendarState.baseEntries = calendarState.baseEntries.filter((entry) => {
        return !isSourceMatch(entry);
    });
    calendarState.optimisticEntries = calendarState.optimisticEntries
        .filter((entry) => !isSourceMatch(entry));
    reconcileEntries();
}

/**
 * Replaces a temporary optimistic entry with definitive source metadata.
 * @param {string} optimisticId Optimistic entry id.
 * @param {object} entry Definitive display-safe entry data.
 * @returns {string} Optimistic entry id.
 */
export function replaceOptimisticCalendarEntry(optimisticId, entry) {
    removeOptimisticCalendarEntry(optimisticId);
    return upsertOptimisticCalendarEntry({
        ...entry,
        optimisticId,
    });
}

/**
 * Stores Calendar listener state for route rendering.
 * @param {string} status Listener status.
 * @param {Error|null} [error=null] Listener error.
 * @returns {void}
 */
export function setCalendarListenerStatus(status, error = null) {
    calendarState.listenerStatus = status;
    calendarState.listenerError = error;
}

/**
 * Stores the active Calendar route month window.
 * @param {Array<string>} monthKeys Loaded month keys.
 * @returns {void}
 */
export function setCalendarLoadedMonthKeys(monthKeys) {
    calendarState.loadedMonthKeys = Array.isArray(monthKeys) ? monthKeys : [];
}

/**
 * Stores month keys whose route listener hit the per-month entry cap.
 * @param {Array<string>} monthKeys Overflowing month keys.
 * @returns {void}
 */
export function setCalendarOverflowMonthKeys(monthKeys) {
    calendarState.overflowMonthKeys = Array.isArray(monthKeys) ? monthKeys : [];
}

/**
 * Stores the visible Calendar month range.
 * @param {string} startMonth Start month key.
 * @param {string|null} endMonth End month key.
 * @returns {void}
 */
export function setCalendarVisibleMonths(startMonth, endMonth = null) {
    calendarState.visibleStartMonth = startMonth || null;
    calendarState.visibleEndMonth = endMonth || startMonth || null;
}

/**
 * Resets route-local Calendar state.
 * @returns {void}
 */
export function resetCalendarState() {
    calendarState.baseEntries = [];
    calendarState.entries = [];
    calendarState.optimisticEntries = [];
    calendarState.listenerStatus = "idle";
    calendarState.listenerError = null;
    calendarState.visibleStartMonth = null;
    calendarState.visibleEndMonth = null;
    calendarState.loadedMonthKeys = [];
    calendarState.overflowMonthKeys = [];
}
